import { useFormContext } from 'react-hook-form';
import { AllocationTaxStatusDTO, TaxStatusDTO } from '../../../../../../../../api/useAccountsQuery';
import { getDefaultAssetAllocation } from '../../../../../../../../helpers';
import { AssetAllocationList, FormFields } from '../../../../AccountForm.meta';
import { useAccountFormContext } from '../../../../context/useAccountFormContext';
import { Allocation, getWeightedAllocation } from '../../utils/getWeightedAllocation';

interface UseNonSpecificPositionsParams {
  assetAllocationTaxStatus: AllocationTaxStatusDTO | null;
}

export const useNonSpecificPositions = ({
  assetAllocationTaxStatus,
}: UseNonSpecificPositionsParams) => {
  const { watch } = useFormContext<FormFields>();

  const [
    accountTaxStatus,
    accountType,
    balances,
    baseNonSpecificPositionsValue,
    assetAllocationList,
    annuityIsAnnuitized,
  ] = watch([
    'bankAccount.TaxStatus',
    'bankAccount.AccountType',
    'bankAccount.Balances',
    'bankAccount.NonSpecificPositionsValue',
    'accountPortfolio.assetAllocationList',
    'bankAccount.AnnuityIsAnnuitized',
  ]);

  const {
    options: { DefaultAssetAllocation },
  } = useAccountFormContext();

  const defaultAssetAllocation = getDefaultAssetAllocation(DefaultAssetAllocation, {
    AccountType: accountType!,
    TaxStatus: assetAllocationTaxStatus!,
    AnnuityIsAnnuitized: annuityIsAnnuitized,
  });

  if (accountTaxStatus !== TaxStatusDTO.Mix) {
    if (!baseNonSpecificPositionsValue) {
      return {
        data: null,
      };
    }

    const allocationsWithoutTaxStatus = assetAllocationList.find(
      ({ taxStatus }) => taxStatus === null,
    ) as AssetAllocationList;

    const aggregatedPositions = [
      {
        allocation: allocationsWithoutTaxStatus.assetAllocation.reduce((allocationAcc, cur) => {
          return {
            ...allocationAcc,
            [cur.id]: Number(cur.y) / 100,
          };
        }, {}),
        total: Number(baseNonSpecificPositionsValue),
      },
    ] as Allocation[];

    const weightedAllocation = getWeightedAllocation(aggregatedPositions);

    const chartValues = defaultAssetAllocation.map(asset => ({
      ...asset,
      y: weightedAllocation
        ? (weightedAllocation[asset.id] ?? 0) * 100
        : (asset.y ?? 0) * 100,
    }));

    return {
      data: {
        aggregatedPositions,
        chartValues,
      },
    };
  }

  if (!balances) {
    return {
      data: null,
    };
  }

  const nonSpecificPositionsWithValueKeys =
    assetAllocationTaxStatus === null
      ? Object.keys(balances).filter(key => balances[key].NonSpecificPositionsValue)
      : [TaxStatusDTO[assetAllocationTaxStatus]];

  const aggregatedPositions = nonSpecificPositionsWithValueKeys.reduce((acc, key) => {
    const assetAllocationByTaxStatus = assetAllocationList.find(
      ({ taxStatus }) => taxStatus === TaxStatusDTO[key],
    );

    if (!assetAllocationByTaxStatus) {
      return [
        ...acc,
        {
          allocation: defaultAssetAllocation.reduce((allocationAcc, cur) => {
            return {
              ...allocationAcc,
              [cur.id]: Number(cur.y),
            };
          }, {}),
          total: 100,
        },
      ];
    }

    const { assetAllocation } = assetAllocationByTaxStatus;

    if (!balances[key].NonSpecificPositionsValue) {
      return acc;
    }

    return [
      ...acc,
      {
        allocation: assetAllocation.reduce((allocationAcc, cur) => {
          return {
            ...allocationAcc,
            [cur.id]: Number(cur.y) / 100,
          };
        }, {}),
        total: balances[key].NonSpecificPositionsValue,
      },
    ];
  }, [] as Allocation[]);

  if (aggregatedPositions.length === 0) {
    return {
      data: null,
    };
  }

  const weightedAllocation = getWeightedAllocation(aggregatedPositions);

  const chartValues = defaultAssetAllocation.map(asset => ({
    ...asset,
    y: weightedAllocation
      ? (weightedAllocation[asset.id] ?? 0) * 100
      : (asset.y ?? 0) * 100,
  }));

  return {
    data: {
      aggregatedPositions,
      chartValues,
    },
  };
};
